import React, { createContext, useState } from 'react'
import Swal from 'sweetalert2'

import { uploadNewVideo, getVideoJobStatus } from "../services/api/VideoDataService"
import {
    uploadProfilePicture,
    removeProfilePicture,
} from "../services/api/UserDataService"
import { CropModal } from "../pages/studio/tabs/settings/CropModal"
import { resolveMediaUrl } from "../utils/media/resolveMediaUrl"
import { useAuth } from "./SecurityContextProvider"


export const MediaContext = createContext()

// Cheap client-side limits, mirroring the backend (which re-validates everything).
const VIDEO_MAX_BYTES = 1_073_741_824 // 1 GB
const VIDEO_MAX_SECONDS = 60

// Background-processing poll cadence. The cap (~5 min) matches the backend ffmpeg timeout.
const POLL_INTERVAL_MS = 2000
const POLL_MAX_TRIES = 150

// Best-effort duration read via a throwaway <video> element. Some codecs won't report it
// client-side; the backend re-checks with ffprobe regardless.
const readVideoDuration = (file) =>
    new Promise((resolve) => {
        const tempVideo = document.createElement('video')
        tempVideo.preload = 'metadata'
        tempVideo.onloadedmetadata = () => {
            URL.revokeObjectURL(tempVideo.src)
            resolve(tempVideo.duration)
        }
        tempVideo.onerror = () => {
            URL.revokeObjectURL(tempVideo.src)
            resolve(0)
        }
        tempVideo.src = URL.createObjectURL(file)
    })

// Maps a backend video error code to a friendly message.
const uploadErrorMessage = (error) => {
    switch (error?.response?.data?.code) {
        case 'VIDEO_TOO_LARGE': return 'Video must be under 1 GB.'
        case 'VIDEO_TOO_LONG': return 'Video must be 60 seconds or shorter.'
        case 'INVALID_FILE_TYPE': return "That file isn't a supported video (use MP4, MOV, WebM, or AVI)."
        case 'NO_VIDEO_STREAM': return 'That file has no video track.'
        case 'PROCESSING_FAILED': return "We couldn't process that video. Please try a different file."
        case 'PROCESSING_TIMEOUT': return 'Processing took too long. Please try a shorter or smaller video.'
        case 'UPLOAD_IN_PROGRESS': return 'You already have an upload in progress. Please wait for it to finish.'
        default: return 'There was a problem uploading your video. Please try again.'
    }
}

export const MediaProvider = ({ user, page, children }) => {
    const { setUserData } = useAuth()
    const [videoSrc, setVideoSrc] = useState(resolveMediaUrl(page?.videoSrc) || "")
    const [cropModalOpen, setCropModalOpen] = useState(false)
    const [selectedImageSrc, setSelectedImageSrc] = useState(null)
    const [selectedFileName, setSelectedFileName] = useState('')


    const handleDeleteProfilePic = async () => {
        Swal.fire({
            title: "Remove profile picture?",
            text: "This action cannot be undone!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Remove",
            customClass: {
                confirmButton: "swal2-confirm-danger",
                cancelButton: "swal2-cancel-neutral",
            }
        })
            .then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        await removeProfilePicture()
                        setUserData(prev => ({ ...prev, profilePicture: null }))
                        Swal.fire({
                            title: "Removed!",
                            text: "Your profile picture has been removed.",
                            icon: "success"
                        })
                    } catch (error) {
                        Swal.fire({
                            title: "Opps, Something went wrong!",
                            text: "Failed to remove profile picture. Please try again later.",
                            icon: "error"
                        })
                    }
                }
            })

    }

    const handleUpdateProfilePic = async () => {
        const { value: file } = await Swal.fire({
            title: "Select image",
            input: "file",
            inputAttributes: {
                "accept": "image/*",
                "aria-label": "Upload profile picture"
            }
        })
        if (file) {
            const reader = new FileReader()
            reader.onload = (e) => {
                setSelectedImageSrc(e.target.result)
                setSelectedFileName(file.name)
                setCropModalOpen(true)
            }
            reader.readAsDataURL(file)
        }
    }

    const handleCropComplete = async (croppedBlob) => {
        setCropModalOpen(false)

        Swal.fire({
            title: 'Uploading...',
            html: 'Please wait. Uploading your image.',
            allowOutsideClick: false,
            showConfirmButton: false,
            willOpen: () => {
                Swal.showLoading()
            }
        })

        const file = new File([croppedBlob], selectedFileName, { type: 'image/jpeg' })
        const formData = new FormData()
        formData.append("file", file)

        try {
            const updated = await uploadProfilePicture(formData)
            Swal.close()
            const previewUrl = URL.createObjectURL(croppedBlob)
            Swal.fire({
                title: "Image upload successful!",
                html: `<img
                    src="${previewUrl}"
                    alt="Your browser does not support the img tag"
                    width="240"
                    height="240"
                    style="border-radius: 50%"
                />`,
                icon: "success",
            })
            setUserData(updated)
        } catch (error) {
            Swal.close()
            Swal.fire({
                title: "Upload failed",
                text: "There was a problem with the upload.",
                icon: "error",
            })
        }
    }

    const uploadVideo = async () => {
        const { value: file } = await Swal.fire({
            title: "Upload Video",
            text: "up to 1-minute video (mp4, mov)",
            input: "file",
            inputAttributes: {
                accept: "video/*",
                "aria-label": "Upload your video",
            },
            confirmButtonText: "Done",
        })

        if (!file) {
            return
        }

        // --- cheap client-side pre-checks; the backend re-validates everything ---
        if (!file.type.startsWith('video/')) {
            Swal.fire({
                title: 'Invalid file type',
                text: 'Please upload a video file (MP4, MOV).',
                icon: 'warning',
            })
            return
        }

        if (file.size > VIDEO_MAX_BYTES) {
            Swal.fire({
                title: 'Video too large',
                text: 'Video must be under 1 GB.',
                icon: 'warning',
            })
            return
        }

        const duration = await readVideoDuration(file)
        if (duration > VIDEO_MAX_SECONDS) {
            Swal.fire({
                title: 'Video too long',
                text: 'Video must be 60 seconds or shorter.',
                icon: 'warning',
            })
            return
        }

        // --- upload (blocking only during the network transfer) ---
        Swal.fire({
            title: 'Uploading...',
            html: 'Please wait while we upload your video.',
            allowOutsideClick: false,
            showConfirmButton: false,
            willOpen: () => Swal.showLoading(),
        })

        const formData = new FormData()
        formData.append("file", file)

        let jobId
        try {
            // The backend validates synchronously, then transcodes in the background and returns 202.
            const accepted = await uploadNewVideo(formData)
            jobId = accepted.jobId
        } catch (error) {
            // Synchronous rejections (bad type/size/duration, upload-in-progress) come back here.
            Swal.fire({
                title: "Upload failed",
                text: uploadErrorMessage(error),
                icon: "error",
            })
            return
        }

        // Upload accepted — free the user with a non-blocking toast while processing continues.
        Swal.fire({
            toast: true,
            position: 'top-end',
            title: 'Upload done',
            html: "We're processing your video for your page. This should only take a moment.",
            showConfirmButton: false,
            allowOutsideClick: true,
            didOpen: () => Swal.showLoading(),
        })

        pollVideoJob(jobId)
    }

    // Polls the background transcode job and, when it finishes, swaps the live preview + toasts the
    // result. A new Swal.fire replaces the "processing" toast (SweetAlert2 shows one popup at a time).
    const pollVideoJob = (jobId) => {
        let tries = 0
        const tick = async () => {
            tries += 1
            let data
            try {
                data = await getVideoJobStatus(jobId)
            } catch (error) {
                // 404 (job lost to a restart) or a transient error — give up once past the cap.
                if (tries >= POLL_MAX_TRIES) {
                    showStillProcessingToast()
                    return
                }
                setTimeout(tick, POLL_INTERVAL_MS)
                return
            }

            if (data.status === 'ready') {
                setVideoSrc(resolveMediaUrl(data.videoSrc)) // live-refreshes the preview pane
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Your video is ready',
                    html: 'Click on preview to see how amazing it looks.',
                    showConfirmButton: false,
                    timer: 6000,
                    timerProgressBar: true,
                })
                return
            }
            if (data.status === 'failed') {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: 'Processing failed',
                    html: uploadErrorMessage({ response: { data: { code: data.code } } }),
                    showConfirmButton: true,
                })
                return
            }
            // still processing
            if (tries >= POLL_MAX_TRIES) {
                showStillProcessingToast()
                return
            }
            setTimeout(tick, POLL_INTERVAL_MS)
        }
        setTimeout(tick, POLL_INTERVAL_MS)
    }

    const showStillProcessingToast = () => {
        Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'info',
            title: 'Still processing',
            html: 'Your video is taking a while. Refresh the page in a moment to check on it.',
            showConfirmButton: true,
        })
    }

    return (
        <MediaContext.Provider value={{
            handleDeleteProfilePic,
            handleUpdateProfilePic,
            uploadNewVideo,
            uploadVideo,
            videoSrc
        }}>
            <CropModal
                open={cropModalOpen}
                imageSrc={selectedImageSrc}
                onClose={() => setCropModalOpen(false)}
                onCropComplete={handleCropComplete}
            />
            {children}
        </MediaContext.Provider>
    )
}